import { Signal, computed, effect, inject, untracked } from '@angular/core'

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'
import { Store } from '@ngrx/store'

import { HarnessConfigService } from '@flowx/designer/modules/ai-builder/services/harness-config.service'
import { HarnessWriteTargetNavigator } from '@flowx/designer/modules/ai-builder/services/harness-write-target.navigator'
import { HarnessSessionStore } from '@flowx/designer/modules/ai-builder/store/harness-session.store'
import { HarnessArtefact } from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import { onChange } from '@flowx/designer/modules/ai-builder/utils/signals.util'
import { selectAppId, selectUrl } from '@flowx/designer/shared/store/selectors/router.selectors'

/** What the builder shows beside the conversation: Make is the designer itself with the rail
 * docked alongside it; Plan takes the frame below the bar. */
export type AiBuilderView = 'make' | 'plan'

/** The rail arriving or leaving. Both are transient — the motion, not a steady open or closed
 * state. Only assist going on and off moves the rail: between the builder's own views the
 * conversation is already at the left edge and simply stays there. */
export type AiBuilderRailMotion = 'idle' | 'entering' | 'leaving'

/** The one place this number exists. The shell stamps it onto `.app-root-container` as
 * `--ai-rail-animation` and its stylesheet animates on that, so the CSS cannot drift from the
 * timer that holds the rail mounted while it leaves — see `railLeaving`. */
export const AI_BUILDER_RAIL_ANIMATION_MS = 280

/** The conversation column's width in pixels on Plan: the design's 383px column plus its own 1px
 * divider, and how far the maker may drag it either way. Make cannot resize the rail, so it docks
 * at the design's column (`--ai-rail-width` on `.app-root-container`) whatever Plan was set to. */
export const AI_BUILDER_RAIL_WIDTH = { default: 384, min: 320, max: 720 } as const

const TEST_RUN_RAIL_KEY = 'flx-ai-builder-test-run-rail'

type AiBuilderState = {
  /** Assist asked for, by the sparkles or ⌘K. Off to begin with and not remembered across
   * projects: a project opens as the designer alone, so assistance is always something asked
   * for. */
  assistRequested: boolean
  view: AiBuilderView
  /** Unsubmitted rail text, shared by Make and Plan. */
  composerDraft: string
  /** The artefact the Plan view was asked to open on; null means whichever arrived last. */
  pinnedArtefact: HarnessArtefact | null
  railMotion: AiBuilderRailMotion
  /** The message whose negative verdict is being explained, or null. Held here rather than on the
   * row that asked: the form covers the whole rail, and a row cannot cover its own ancestor. */
  ratingMessageId: string | null
  /** The conversations panel is over the chat (conversation-owned only). */
  listOpen: boolean
  /** The rail's width on Plan, as the maker last dragged it there. Kept for the session only: a
   * reload brings back the design's column. Make never reads it. */
  railWidth: number
  /** Something on the page (comment mode) is keeping the maker on the designer: a brief or plan
   * landing does not open the Plan view meanwhile. */
  makeViewHeld: boolean
}

const initialState: AiBuilderState = {
  assistRequested: false,
  view: 'make',
  composerDraft: '',
  pinnedArtefact: null,
  railMotion: 'idle',
  ratingMessageId: null,
  listOpen: false,
  railWidth: AI_BUILDER_RAIL_WIDTH.default,
  makeViewHeld: false,
}

/** The AI-assisted builder's rulebook: when it may be on screen, what it shows, and how the rail
 * comes and goes. The conversation itself lives in `HarnessSessionStore`. */
export const AiBuilderStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => {
    const ngrxStore = inject(Store)
    const session = inject(HarnessSessionStore)
    const config = inject(HarnessConfigService)

    return {
      /** Design-time AI is usable here: the maker holds the AI permission of the page they are on
       * AND the environment has the agents deployed. Both halves matter — either alone offers a
       * rail that cannot work. */
      aiAllowed: config.available,
      _appId: ngrxStore.selectSignal(selectAppId),
      _pageLevel: computed(() => config.pageLevel()),
      _url: ngrxStore.selectSignal(selectUrl),
      /** The version the conversation speaks for — on Runtime, the one its Config tab opens. */
      _appVersionId: config.appVersionId,
      /** The one-pager's revision counter: a new one landing is what opens the Plan view. */
      _briefRevision: session.briefRevision,
      /** The plan's: a new one landing releases the space to it. */
      _planRevision: session.planRevision,
      /** True when the latest brief was replayed, whether or not its interrupt is still open. */
      _briefRecovered: session.briefRecovered,
      _sessionId: session.sessionId,
      _writeTargetOpenedRevision: inject(HarnessWriteTargetNavigator).openedRevision,
    }
  }),
  withComputed((store) => {
    /** Assist needs AI access and, on a project, its version, an open conversation or its list.
     * Off a project a conversation is the maker's, not a version's, so it starts there too. */
    const assistAvailable = computed(
      () =>
        store.aiAllowed() &&
        (store._pageLevel() !== 'project' ||
          !!store._appVersionId() ||
          store.listOpen() ||
          !!store._sessionId())
    )
    /** Asked for, and somewhere it can be asked for. Everything the builder puts up hangs off
     * this rather than off `assistRequested` alone. */
    const assistOn = computed(() => assistAvailable() && store.assistRequested())
    const railDocked = computed(() => assistOn() && store.view() === 'make')
    const railLeaving = computed(() => store.railMotion() === 'leaving')

    return {
      assistAvailable,
      assistOn,
      /** Make: the rail beside the designer. */
      railDocked,
      /** The rail is up outside a project, where there is no designer bar to dock under and no
       * designer panel to set into the builder's ground. */
      outsideProject: computed(() => store._pageLevel() !== 'project'),
      /** The conversations panel, over the chat — on Make and in the Plan view alike, the rail
       * being in both and its session bar the way to open the list from either. */
      listShown: computed(() => assistOn() && store.listOpen()),
      /** Plan: the whole frame below the bar. */
      planOpen: computed(() => assistOn() && store.view() === 'plan'),
      railEntering: computed(() => store.railMotion() === 'entering'),
      railLeaving,
      /** The rail's `@if` interrupt: docked, or still on its way out. Both are the rail taking the left
       * edge, so it stays mounted through the leave animation. */
      railMounted: computed(() => railDocked() || railLeaving()),
    }
  }),
  withMethods((store) => ({
    /** The sparkles toggle (the header button and ⌘K alike). Off closes what pressing it opened —
     * a full-frame view, the rail — but keeps the conversation for the next press. */
    toggle: (): void => {
      if (!store.assistAvailable()) {
        return
      }

      if (store.assistRequested()) {
        try {
          sessionStorage.removeItem(TEST_RUN_RAIL_KEY)
        } catch {
          // Storage may be unavailable; toggling assist must still work.
        }
      }

      patchState(
        store,
        store.assistRequested()
          ? { assistRequested: false, view: 'make', listOpen: false }
          : { assistRequested: true }
      )
    },

    setComposerDraft: (composerDraft: string): void => patchState(store, { composerDraft }),

    openView: (view: AiBuilderView): void => patchState(store, { view, pinnedArtefact: null }),

    /** Open the Plan view on a particular artefact — the chip on a reply points at one. */
    openPlan: (artefact: HarnessArtefact): void =>
      patchState(store, { view: 'plan', pinnedArtefact: artefact }),

    /** Folds whatever holds the frame back down to the rail on Make. The plan is transient over
     * the designer, so picking a place in the project is also a way home from it. */
    backToMake: (): void => patchState(store, { view: 'make', pinnedArtefact: null }),

    /** Keep only a Test run's rail across reloads; other pages still start with assist off. */
    rememberTestRun: (path: string): void => {
      const sessionId = store._sessionId()
      if (sessionId) {
        try {
          sessionStorage.setItem(TEST_RUN_RAIL_KEY, JSON.stringify([sessionId, path]))
        } catch {
          // As with conversation recovery, blocked storage only costs reload persistence.
        }
      }
    },

    /** Ask what was wrong with one message. A second press replaces the first rather than
     * stacking, since only one form fits over the rail. */
    explainVerdict: (messageId: string): void => patchState(store, { ratingMessageId: messageId }),

    closeVerdictForm: (): void => patchState(store, { ratingMessageId: null }),

    openList: (): void => patchState(store, { listOpen: true }),

    closeList: (): void => patchState(store, { listOpen: false }),

    /** Keeps the view on Make while held: a document landing waits as a chip. Nothing is opened
     * when the hold lifts, only the next one is free to. */
    holdMakeView: (makeViewHeld: boolean): void => patchState(store, { makeViewHeld }),

    /** Resize the conversation column on Plan, held to the range the plan area can give. */
    resizeRail: (width: number): void =>
      patchState(store, {
        railWidth: Math.min(
          AI_BUILDER_RAIL_WIDTH.max,
          Math.max(AI_BUILDER_RAIL_WIDTH.min, Math.round(width))
        ),
      }),

    /**
     * ⌘K with assist off: turn it on, on Make, with the conversations over the chat. False when
     * assist is already on or AI is not allowed, so the shell hands ⌘K to toggle() instead. The
     * session bar shows and hides the conversations with openList() and closeList().
     */
    openWithConversations: (): boolean => {
      if (!store.aiAllowed() || store.assistOn()) {
        return false
      }

      patchState(store, { assistRequested: true, view: 'make', listOpen: true })

      return true
    },
  })),
  withHooks({
    onInit(store) {
      let testRunRestored = false
      effect(() => {
        const sessionId = store._sessionId()
        const path = store._url()?.split('?')[0]
        if (testRunRestored || !sessionId || !path) {
          return
        }
        try {
          if (sessionStorage.getItem(TEST_RUN_RAIL_KEY) === JSON.stringify([sessionId, path])) {
            testRunRestored = true
            patchState(store, { assistRequested: true, view: 'make' })
          }
        } catch {
          // Browsers that block storage keep the default assist-off behavior.
        }
      })

      trackRailMotion(store.railDocked, store.assistOn, (railMotion) =>
        patchState(store, { railMotion })
      )

      onChange(store._writeTargetOpenedRevision, () => store.backToMake())

      // Not remembered across projects: a project always opens with assist off and an empty
      // composer — unless a conversation is open, which goes with the maker from one to the next.
      // Stepping into or out of a project keeps it: the chat is open outside projects too.
      onChange(store._appId, (appId, previousAppId) => {
        if (appId && previousAppId && !store._sessionId()) {
          patchState(store, {
            assistRequested: false,
            view: 'make',
            listOpen: false,
            composerDraft: '',
          })
        }
      })

      // A one-pager landing opens the Plan view on it: the document is the point of the turn, and
      // a chip in the rail is a smaller thing than the thing it opens. Only a NEW one, and only
      // while the builder is up — a brief that arrives with assist off waits as a chip. A
      // recovered brief is not news and must not move the view, checked on the session's own flag
      // because `assistOn` is true on any re-attach while the builder is up. Held on Make, a one-pager
      // waits as a chip, and is not opened when the hold lifts.
      onChange(store._briefRevision, (revision, seen) => {
        if (
          revision > seen &&
          store.assistOn() &&
          !store._briefRecovered() &&
          !store.makeViewHeld()
        ) {
          patchState(store, { view: 'plan', pinnedArtefact: 'brief' })
        }
      })

      // A plan landing releases the space to it. The brief's auto-open pins the focus, and nothing
      // else cleared it — so after the go-ahead the Plan view kept showing the superseded one-pager
      // beside a button reading "Approve the plan".
      onChange(store._planRevision, (revision, seen) => {
        if (revision > seen) {
          patchState(store, { pinnedArtefact: null })
        }
      })
    },
  })
)

/** Time the rail's animation so the shell only has classes to bind. Returning from Plan also docks
 * the rail, but must not animate: the conversation was already at the left edge. */
function trackRailMotion(
  isRailDocked: Signal<boolean>,
  isAssistOn: Signal<boolean>,
  setMotion: (motion: AiBuilderRailMotion) => void
): void {
  let wasDocked = false
  let wasOn = false
  let idleTimer: ReturnType<typeof setTimeout> | null = null

  const animateRail = (railMotion: AiBuilderRailMotion): void => {
    setMotion(railMotion)

    if (idleTimer) {
      clearTimeout(idleTimer)
    }

    idleTimer = setTimeout(() => setMotion('idle'), AI_BUILDER_RAIL_ANIMATION_MS)
  }

  effect(() => {
    const docked = isRailDocked()
    const on = isAssistOn()

    untracked(() => {
      // Assist just came on: the rail arrives from the left edge.
      if (docked && !wasDocked && !wasOn) {
        animateRail('entering')
      }

      // Assist just went off: the rail leaves, rather than the Plan view handing it back.
      if (!docked && wasDocked && !on) {
        animateRail('leaving')
      }

      wasDocked = docked
      wasOn = on
    })
  })
}
